import { jSith } from "../jquery-replacement";
import { DeepOmit, ValueElseUndefined } from "../../pojo/Util";
import { S25Array } from "./s25.array";
import { S25Date } from "./s25.date";
import { S25DateParse } from "./s25.date.parse";
import { S25String } from "./s25.string";
import { S25Number } from "./s25.number";
import { isArray, isDate, isDefined, isFunction, isObject, isUndefined } from "./index";

interface PropertyFindParams {
    json: any;
    properties: string | string[];
    retValue?: boolean;
    resultArr?: any[];
    actionOnResult?: (json: any, key: any) => void;
    searchValue?: any;
    cache?: any[];
    startsWith?: boolean;
}

export namespace S25ObjectUtil {
    export function camelToSnakeObj(obj: any): any {
        return transformKeys(obj, S25String.camelToSnake);
    }

    export function transformKeys(obj: any, transform: (key: string) => string, shallow: boolean = false): any {
        return transformKeysDeep(obj, transform, shallow);
    }

    function transformKeysDeep(
        obj: any,
        transform: (key: string) => string,
        shallow: boolean = false,
        seen: WeakMap<any, any> = new WeakMap(),
    ): any {
        const ret: any = {};
        for (const key in obj) {
            if (obj.hasOwnProperty(key)) {
                const newKey = transform(key);
                const value = obj[key];
                if (isObject(value) && !isDate(value) && !shallow) {
                    if (seen.has(value)) {
                        ret[newKey] = seen.get(value);
                    } else {
                        ret[newKey] = {};
                        seen.set(value, ret[newKey]);
                        const newValue = transformKeysDeep(value, transform, shallow, seen);
                        Object.assign(ret[newKey], newValue);
                    }
                } else {
                    ret[newKey] = value;
                }
            }
        }
        return ret;
    }
    export function setOnObjectDeep(obj: any, prop: string, value: any, cache?: any, override?: boolean): any {
        cache = cache || [];
        if (isObject(obj) && !isDate(obj) && !refEq(obj, cache)) {
            cache.push(obj);
            jSith.forEach(obj, (_: any, childObj: any) => {
                setOnObjectDeep(childObj, prop, value, cache);
            });

            obj[prop] = override ? value : jSith.coalesce(obj[prop], value);
        }
    }
    export function refEq(comp: any, cache: any[]): any {
        if (cache && cache.length) {
            for (let i = cache.length - 1; i >= 0; i--) {
                if (cache[i] === comp) {
                    return { i: i };
                }
            }
            return null;
        } else {
            return null;
        }
    }

    export function skipObjHelper<SkipMap extends object>(key: string, val: any, skipMap?: SkipMap): boolean {
        if (skipMap && key in skipMap) return true;
        if (!val) return false;

        if (key && key.indexOf("$") === 0) return true;
        if (val.constructor === Window) return true;
        if (val._zoneDelegate || val._zone || val.__ngContext__) return true;
        if (val instanceof HTMLElement) return true;
        if (val.constructor && val.constructor === window.angBridge?.$rootScope?.constructor) return true;
        if (typeof window.jQuery === "function" && val instanceof window.jQuery) return true;

        return false;
    }

    export function deepCopy<T, Y extends any = never>(
        json: T,
        skipMap?: any,
        cache?: Map<any, any>,
    ): ValueElseUndefined<Y> extends undefined ? T : DeepOmit<T, keyof Y> {
        if (skipObjHelper("", json, skipMap)) {
            return null;
        }

        let ret: any = null;
        const jsonIsArray = isArray(json);

        if (jsonIsArray) {
            ret = [];
        } else if (isDate(json)) {
            return S25DateParse.clone(json) as any;
        } else if (json instanceof Set) {
            cache = cache || new Map<any, any>();
            const copiedSet = new Set<any>();
            cache.set(json, copiedSet);
            for (const entry of json) {
                copiedSet.add(deepCopy(entry, skipMap, cache));
            }
            return copiedSet as any;
        } else if (isObject(json)) {
            ret = {};
        } else {
            return json as unknown as any;
        }

        cache = cache || new Map<any, any>();

        if (json) {
            for (const key in json) {
                if (json.hasOwnProperty && json.hasOwnProperty(key)) {
                    if (jsonIsArray && !S25Number.isParsableAsInt(key)) {
                        continue;
                    }

                    const val = json[key];

                    if (skipObjHelper(key, val, skipMap)) {
                        continue;
                    }

                    if (cache.has(val)) {
                        ret[key] = cache.get(val);
                    } else if (isDate(val)) {
                        ret[key] = S25DateParse.clone(val);
                    } else if (isFunction(val)) {
                        ret[key] = val;
                    } else if (isArray(val)) {
                        //loop array
                        const arr = val as unknown as any[];
                        ret[key] = [];
                        cache.set(val, ret[key]);
                        for (let i = 0; i < arr.length; i++) {
                            if (skipObjHelper(key, arr[i], skipMap)) {
                                continue;
                            }
                            ret[key].push(deepCopy(arr[i], skipMap, cache));
                        }
                    } else if (val instanceof Set) {
                        ret[key] = new Set<any>();
                        cache.set(val, ret[key]);
                        for (const entry of val) {
                            ret[key].add(deepCopy(entry, skipMap, cache));
                        }
                    } else if (isObject(val)) {
                        //loop obj
                        ret[key] = {};
                        cache.set(val, ret[key]);
                        for (const prop in val) {
                            if (!Object.prototype.hasOwnProperty.call(val, prop)) continue;
                            if (skipObjHelper(prop, val[prop], skipMap)) continue;
                            ret[key][prop] = deepCopy(val[prop], skipMap, cache);
                        }
                    } else if (["string", "number", "boolean", "undefined", "null"].indexOf(typeof val) > -1) {
                        //handle key/val
                        ret[key] = val;
                    }
                    cache.set(val, ret[key]);
                }
            }
            return ret;
        } else {
            return json as unknown as any;
        }
    }
    export function deleteFalseProperties(item: any): void {
        for (const prop in item) {
            if (item.hasOwnProperty(prop)) {
                item[prop] === false && delete item[prop];
            }
        }
    }
    export function propertyGet<T = any>(json: any, properties: string | string[]): T | undefined {
        return propertyFind({ json: json, properties, retValue: true });
    }

    export function propertyGetVal(json: any, property: string) {
        return jSith.coalesce(propertyGet(json, property), "");
    }

    export function propertyGetAll(json: any, properties: string | string[]) {
        const resultArr: any[] = [];
        propertyFind({ json, properties, resultArr });
        return resultArr;
    }

    export function propertyGetAllUnique(json: any, property: string) {
        return S25Array.unique(propertyGetAll(json, property));
    }

    export function propertyDelete(json: any, properties: string | string[]) {
        propertyFind({
            json,
            properties,
            // the empty [] array causes propertyFind to accumulate results instead of short-circuit at first one found so that we delete ALL matches
            resultArr: [],
            actionOnResult: function (json: any, key: any) {
                delete json[key];
            },
        });
    }

    export function propertyDeleteStartsWith(json: any, startsWithProperty: any) {
        propertyFind({
            json: json,
            properties: startsWithProperty,
            // the empty [] array for "resultArr" causes propertyFind to accumulate results instead of short-circuit at first one found so that we delete ALL matches
            resultArr: [],
            actionOnResult: function (json: any, key: any) {
                delete json[key];
            },
            startsWith: true,
        });
    }

    export function propertyDeleteByValue(json: any, property: any, value: any) {
        property = S25Array.forceArray(property);
        for (let i = 0; i < property.length; i++) {
            //empty resultArr to delete ALL matches
            valueFindAction(json, property[i], value, [], function (json: any, key: any) {
                delete json[key];
            });
        }
    }

    export function propertyGetParentWithChildValue(json: any, properties: string | string[], value: any) {
        let parent: any = null;
        propertyFind({
            json: json,
            properties: properties,
            actionOnResult: function (json: any) {
                parent = json;
            },
            searchValue: value,
        });
        return parent;
    }

    export function propertyGetParentsWithChildValue(json: any, properties: string | string[], searchValue: any) {
        const ret: any[] = [];
        propertyFind({
            json,
            properties,
            resultArr: [],
            actionOnResult: function (json: any) {
                ret.push(json);
            },
            searchValue,
        });
        return ret;
    }

    export function valueFindAction(
        json: any,
        properties: string | string[],
        value: any,
        resultArr?: any,
        actionOnResult?: (json: any, key: any) => void,
    ) {
        return propertyFind({
            json,
            properties,
            resultArr,
            actionOnResult,
            searchValue: jSith.coalesce(value, ""),
        });
    }

    export function valueFind(json: any, property: any, value: any): boolean {
        return valueFindAction(json, property, value, null, undefined);
    }

    //breadth first search for property; if given value then also property value; if resultArr accumulate findings; if actionOnResult run fn on findings
    export function propertyFind({
        json,
        properties,
        retValue,
        resultArr,
        actionOnResult,
        searchValue,
        cache,
        startsWith,
    }: PropertyFindParams) {
        const ret: any = undefined,
            queue: any[] = [];
        cache = cache || [];
        const propArray = S25Array.forceArray(properties);

        if (json) {
            queue.push(json);
            while (queue.length > 0) {
                const item = queue.shift();
                if (item && item.hasOwnProperty) {
                    for (const key in item) {
                        if (item.hasOwnProperty(key)) {
                            const value = item[key];
                            //if match...
                            if (
                                keyMatchesProperties({ key, properties: propArray, startsWith }) &&
                                (isUndefined(searchValue) || value == searchValue)
                            ) {
                                actionOnResult?.(item, key);
                                if (resultArr) {
                                    //only add if either NOT an object or if so, if we haven't already processed it
                                    //we add to cache below so the first match is not already in the cache
                                    if (!isObject(value) || cache.indexOf(value) === -1) {
                                        if (isArray(value)) {
                                            //if array itself, flatten into resultArr
                                            Array.prototype.push.apply(resultArr, value);
                                        } else {
                                            resultArr.push(value);
                                        }
                                    }
                                } else {
                                    return retValue ? value : true;
                                }
                            } else if (isObject(value)) {
                                //process children
                                //do not process dollar prefixed keys, window object, scope objects, html and jquery elements
                                //note that checking at the branch expansion point in BFS allows us to still match on any
                                //of these keys and add to results / return from the fn. All these checks do is ensure that
                                //we do not EXPAND into these items bc they are problematic, very deep, and we'd never search on them
                                if (
                                    (key &&
                                        key.indexOf &&
                                        //allow $$state so we can search promise values
                                        key !== "$$state" &&
                                        // skip $ or __zone_ prefixed keys
                                        (key.indexOf("$") === 0 || key.indexOf("__zone_") === 0)) ||
                                    (value &&
                                        (isFunction(value) ||
                                            value.elementRef || // do not recurse into Angular components
                                            value.constructor === Window ||
                                            (window.angBridge &&
                                                window.angBridge.$rootScope &&
                                                value.constructor === window.angBridge.$rootScope.constructor) ||
                                            value instanceof HTMLElement ||
                                            (typeof window.jQuery === "function" && value instanceof window.jQuery))) ||
                                    //skip circular reference
                                    cache.indexOf(value) > -1
                                ) {
                                    continue;
                                }

                                //if we reach here (continue skips and starts next iter), then we push to BFS queue
                                queue.push(value);
                            }

                            //add objects to cache so we can check for circular references
                            isObject(value) && cache.push(value);
                        }
                    }
                }
            }
            return ret;
        } else {
            return ret;
        }
    }

    function keyMatchesProperties({
        key,
        properties,
        startsWith,
    }: {
        key: string;
        properties: string[];
        startsWith?: boolean;
    }) {
        return (
            properties.includes(key) ||
            (startsWith && key.startsWith && properties.some((prop) => key.startsWith(prop)))
        );
    }
    export function stringify(json: any, cache?: any[]): string {
        if (["number", "string", "boolean"].indexOf(typeof json) > -1) {
            return json;
        } else if (isDate(json)) {
            return S25Date.toS25ISODateTimeStr(json);
        }

        function prefix(isOuterArray: boolean, key: any) {
            return isOuterArray ? "" : '"' + key + '":';
        }

        let ret = "";
        cache = cache || [];
        if (json) {
            const keys = Object.keys(json);
            const isOuterArray = isArray(json);
            const childStrings: string[] = [];
            for (let i = 0; i < keys.length; i++) {
                const key = keys[i];
                const val = json[key];

                if (isObject(val)) {
                    if (refEq(val, cache)) {
                        //if object ref already in cache, skip
                        continue;
                    } else {
                        //push object ret to cache
                        cache.push(val);
                    }
                }

                //skip scope, dollar, window, HTMLElement, jQuery
                if (skipObjHelper(key, val)) {
                    continue;
                }

                if (isDate(val)) {
                    childStrings.push(prefix(isOuterArray, key) + JSON.stringify(val));
                } else if (isFunction(val)) {
                    childStrings.push(prefix(isOuterArray, key) + "FUNCTION");
                } else if (isArray(val)) {
                    //loop array
                    childStrings.push(prefix(isOuterArray, key) + stringify(val, cache));
                } else if (isObject(val)) {
                    //recur obj
                    childStrings.push(prefix(isOuterArray, key) + stringify(val, cache));
                } else {
                    //handle key/val
                    childStrings.push(prefix(isOuterArray, key) + JSON.stringify(val));
                }
            }

            childStrings.sort(); //sort child strings for deterministic comparisons
            ret += childStrings.join(",");

            return (isOuterArray ? "[" : "{") + ret + (isOuterArray ? "]" : "}");
        } else {
            return ret;
        }
    }

    export function dfs(
        obj: any,
        func: (node: any, parentNode?: any, childKey?: any) => any,
        childrenProp?: string,
        visited?: any,
        parentNode?: any,
        childKey?: any,
    ) {
        visited = visited || [];
        const isArrayNode = isArray(obj);
        const isObjectNode = !isArrayNode && isObject(obj);

        if (isArrayNode || isObjectNode) {
            if (visited.indexOf(obj) > -1) {
                return;
            } else {
                visited.push(obj);
            }
        }

        obj && func(obj, parentNode, childKey);

        let children: any = [];
        if (childrenProp) {
            children = (obj && obj[childrenProp] && S25Array.forceArray(obj[childrenProp])) || [];
        } else if (isArrayNode || isObjectNode) {
            children = obj;
        }

        jSith.forEach(children, (key: any, val: any) => {
            dfs(val, func, childrenProp, visited, obj, key);
        });
    }

    export function bsf(obj: any, func: (node: any, parentNode?: any, childKey?: any) => any, childrenProp?: string) {
        const visited: any[] = [];
        const queue: any[] = [];
        obj &&
            queue.push({
                parentNode: null,
                childKey: null,
                node: obj,
            });

        while (queue.length) {
            const entry = queue.shift();

            const isArrayNode = isArray(entry.node);
            const isObjectNode = !isArrayNode && isObject(entry.node);

            if (isArrayNode || isObjectNode) {
                if (visited.indexOf(entry.node) > -1) {
                    return;
                } else {
                    visited.push(entry.node);
                }
            }

            entry && func(entry.node, entry.parentNode, entry.childKey);

            let children = [];
            if (childrenProp) {
                children =
                    (entry.node && entry.node[childrenProp] && S25Array.forceArray(entry.node[childrenProp])) || [];
            } else if (isArrayNode || isObjectNode) {
                children = entry.node;
            }

            jSith.forEach(children, (key: any, val: any) => {
                queue.push({
                    parentNode: entry.node,
                    childKey: key,
                    node: val,
                });
            });
        }
    }

    export function replaceDeep(json: any, replaceValueMap: any): any {
        jSith.forEach(json, function (key: any, obj: any) {
            if (typeof json[key] !== "object" || isDate(json[key])) {
                if (isDefined(replaceValueMap[key])) {
                    if (isFunction(replaceValueMap[key])) {
                        json[key] = replaceValueMap[key](json[key], json); //pass value and parent to function
                    } else {
                        json[key] = replaceValueMap[key]; //replace value with map value
                    }
                }
            } else if (isArray(json[key])) {
                replaceDeep(json[key], replaceValueMap);
            } else if (typeof json[key] === "object") {
                replaceDeep(json[key], replaceValueMap); //recurse through object
            }
        });
        return json; //return replaced json
    }

    //removeUndefined deleteUndefined removeNull deleteNull
    export function deleteUndefDeep(json: any): any {
        jSith.forEach(json, function (key: any, obj: any) {
            if (isUndefined(json[key])) {
                delete json[key];
            } else {
                if (typeof json[key] === "object" && !isDate(json[key])) {
                    deleteUndefDeep(json[key]);
                }
            }
        });
        return json; //return replaced json
    }

    // extend JS object like CSS
    export function extendDeep(dst: any, ...args: any): any {
        dst = jSith.coalesce(dst, {});
        jSith.forEach(args, function (_: any, obj: any) {
            if (obj && obj !== dst) {
                jSith.forEach(obj, function (key: any, value: any) {
                    //note: we do not descend into arrays, etc so do not use isObject as it uses typeof and arrays are typeof "object"
                    if (dst[key] && dst[key].constructor && dst[key].constructor === Object) {
                        extendDeep(dst[key], value);
                    } else {
                        dst[key] = value;
                    }
                });
            }
        });
        return dst;
    }
    export function clone(obj: any) {
        return copy({}, deepCopy(obj));
    }

    export function copy(dest: any, src: any) {
        const destIsArray = isArray(dest);

        if (dest === src) {
            throw Error("Destination equals source");
        } else {
            const cp = deepCopy(src);

            //remove all entries / properties from dst
            jSith.forEach(
                dest,
                function (key: any, value: any) {
                    if (destIsArray) {
                        dest.splice(key, 1);
                    } else {
                        delete dest[key];
                    }
                },
                true,
            );

            //add each entry / property from copy of source into dst
            jSith.forEach(cp, function (key: any, value: any) {
                if (destIsArray) {
                    dest.push(value);
                } else {
                    dest[key] = value;
                }
            });

            return dest;
        }
    }
    export function coalesceDeep(target: any, ...args: any[]): any {
        return _merge(true, target, args);
    }

    export function merge(target: any, ...args: any[]): any {
        return _merge(false, target, args);
    }

    function _merge(coalesce: boolean, target: any, args: any[]): any {
        target = jSith.coalesce(target, {});
        jSith.forEach(S25Array.forceArray(args), function (_: any, obj: any) {
            //loop all arguments (special js variable for all args passed to function)
            if (obj !== target) {
                //do not re-loop target (first argument)
                jSith.forEach(obj, function (key: any, value: any) {
                    //loop all values in this argument
                    //step through target by argument value's keys, as they should match only at the same recursion level
                    if (isObject(target[key]) && !isDate(target[key]) && !isArray(target[key])) {
                        //if target at this key is an object, recurse further down
                        _merge(coalesce, target[key], value);
                    } else if (isArray(target[key])) {
                        //if target is array, we can't assume value has exact index structure...
                        if (isArray(value) && value.length) {
                            //value must be array and have values to be added to target
                            //in order to augment target arrays with value arrays, we need some "id" property to only augment the matching object in the array
                            //so we get target and value props ending in "_id" and choose the first property both have in common
                            let targetIds: any[] = []; //target props ending in "_id"
                            let valueIds: any[] = []; //value props ending in "_id"
                            let commonIds: any[] = []; //common props

                            for (let i = 0; i < value.length; i++) {
                                let matchFound = false;
                                const valueArrEntry = value[i];
                                if (i === 0 && isObject(valueArrEntry)) {
                                    //only need to do this once as the id prop will be on each entry
                                    valueIds = Object.keys(valueArrEntry).filter(function (k) {
                                        return (
                                            k &&
                                            k.toLowerCase &&
                                            (k.toLowerCase().endsWith("_id") ||
                                                k.toLowerCase().endsWith("address_type"))
                                        );
                                    }); //Address nodes don't include and _id
                                }

                                for (let j = 0; j < target[key].length; j++) {
                                    const targetArrEntry = target[key][j];

                                    if (j === 0 && isObject(targetArrEntry)) {
                                        //only need to do this once as the id prop will be on each entry
                                        targetIds = Object.keys(targetArrEntry).filter(function (k) {
                                            return (
                                                k &&
                                                k.toLowerCase &&
                                                (k.toLowerCase().endsWith("_id") ||
                                                    k.toLowerCase().endsWith("address_type"))
                                            );
                                        }); //Address nodes don't include and _id
                                        commonIds = S25Array.shallowIntersection(
                                            targetIds,
                                            valueIds,
                                            S25Array.unique([].concat(targetIds, valueIds)),
                                        );
                                    }

                                    //if we have common ids, choose the first one, then compare entries based on that id prop
                                    //and if same, merge them
                                    if (commonIds.length) {
                                        const idProp = commonIds[0];
                                        if (targetArrEntry[idProp] == valueArrEntry[idProp]) {
                                            matchFound = true;
                                            _merge(coalesce, targetArrEntry, valueArrEntry);
                                        }
                                    }
                                }

                                //if we actually had common ids and never found a match, push value entry to target entries as a new entry in target
                                if (!matchFound) {
                                    target[key].push(valueArrEntry);
                                }
                            }
                        }
                    } else {
                        if (coalesce) {
                            target[key] = jSith.coalesce(target[key], value);
                        } else {
                            target[key] = value;
                        }
                    }
                });
            }
        });
        return target;
    }
    export function isEmptyBean(obj: any): boolean {
        if (isUndefined(obj)) {
            return true;
        }

        for (const key in obj) {
            if (obj.hasOwnProperty(key)) {
                const field: any = obj[key];

                if (isUndefined(field)) {
                    continue;
                }

                if (typeof field === "string" && field.length > 0) {
                    return false;
                }

                if (typeof field === "number") {
                    return false;
                }

                if (typeof field === "object") {
                    if (!isEmptyBean(field)) {
                        return false;
                    }
                }
            }
        }
        return true;
    }

    export function isNonEmptyBean(obj: any) {
        return !isEmptyBean(obj);
    }
}
